import React, { useEffect, useRef } from "react";
import Select from "react-select";
import InfiniteLoader from 'react-window-infinite-loader';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList as List } from 'react-window';
import { useTheme } from "../../../components/hooks/useTheme";

type CustomerSelectWrapperProps = {
    hasNextPage: boolean,
    isNextPageLoading: boolean,
    options: any[],
    loadNextPage: () => void,
    onChange: (value: number) => void,
    setSearchValue: (value: string) => void,
    setPage: (value: number) => void,
    isSearching: boolean,
    setIsSearching: (value: boolean) => void,
    baseWidth?: string;
    height?: number;
    selectedOption: any;
    placeholder?: string,
    isDisabled?: boolean
}
const CustomerSelectWrapper = ({ hasNextPage, isNextPageLoading, options, loadNextPage, onChange, setSearchValue, isSearching, setIsSearching, baseWidth = "50%", height = 250, selectedOption, placeholder = "", isDisabled = false }: CustomerSelectWrapperProps) => {
    const { themeStyles } = useTheme();
    const itemCount = hasNextPage ? options.length + 1 : options.length;
    const loadMoreItems = isNextPageLoading ? () => { } : loadNextPage;
    const isItemLoaded = (index: any) => !hasNextPage || index < options.length;
    const listRef = useRef<any>(null);
    const scrollPosRef = useRef<number>(0);
    const selectRef = useRef<any>(null);

    const handleScroll = (e: any) => {
        if (options.length > 0) {
            scrollPosRef.current = e.scrollOffset;
        } else {
            scrollPosRef.current = 0;
        }
    };

    useEffect(() => {
        if (listRef.current) {
            listRef.current.scrollTo(scrollPosRef.current);
        }
    }, [options]);

    const CustomMenuList = ({ children }: any) => {
        const childrenArray = React.Children.toArray(children);
        const Item = ({ index, style, ...rest }: any) => {
            const child = childrenArray[index];

            const { isScrolling, ...validRest } = rest;

            return (
                <div
                    className="drop-down-list"
                    style={{
                        borderBottom: '1px solid rgb(243 234 234 / 72%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        ...style,
                    }}
                    onClick={() => handleChange(options[index])}
                    {...validRest}
                >
                    {isItemLoaded(index) && child ? child : `Loading...`}
                </div>
            );
        };

        return (
            <AutoSizer
                disableHeight
                style={{
                    width: "100%"
                }}>
                {(width: any) => (
                    <InfiniteLoader
                        isItemLoaded={(index) => index < options.length}
                        itemCount={itemCount}
                        loadMoreItems={loadMoreItems}
                        threshold={2}
                    >
                        {({ onItemsRendered, ref }) => (
                            <List
                                className="List"
                                height={options?.length > 0 && !isSearching ? height : 50}
                                itemCount={isSearching ? 0 : itemCount}
                                onScroll={handleScroll}
                                itemSize={35}
                                onItemsRendered={onItemsRendered}
                                initialScrollOffset={scrollPosRef.current}
                                ref={ref}
                                width={width}
                            >
                                {Item}
                            </List>
                        )}
                    </InfiniteLoader>
                )}
            </AutoSizer>
        );
    };

    const handleChange = (selected: any) => {
        onChange(selected);
    };

    return (
        <Select
            ref={selectRef}
            menuPortalTarget={document.body}
            styles={{
                control: (baseStyles, state) => ({
                    ...baseStyles,
                    backgroundColor: themeStyles.topBar.bg,
                    color: themeStyles.topBar.textColor,
                    borderRadius: '8px',
                    borderColor: state.isFocused ? "#167AC3" : themeStyles.topBar.borderColor,
                    "&: hover": {
                        borderColor: "#167AC3",
                    }
                }),
                menu: (base) => ({
                    ...base,
                    fontSize: "12px",
                    backgroundColor: themeStyles.topBar.bg,
                    color: themeStyles.topBar.textColor,
                }),
                menuPortal: base => ({ ...base, zIndex: 9999 }),
                option: (base, state) => ({
                    ...base,
                    backgroundColor: state.isSelected
                        ? "#167AC3"
                        : state.isFocused
                            ? themeStyles.topBar.menuItemsHoverColor
                            : "transparent",
                    color: state.isSelected ? "white" : themeStyles.topBar.textColor,
                    fontWeight: 600
                }),
                input: (base) => ({
                    ...base,
                    color: themeStyles.form.inputTextColor
                }),
                singleValue: (base) => ({
                    ...base,
                    color: themeStyles.form.inputTextColor,
                }),
            }}
            placeholder={placeholder}
            className="basic-single"
            classNamePrefix="select"
            closeMenuOnSelect={true}
            isSearchable={true}
            isClearable={true}
            value={selectedOption}
            onFocus={() => {
                setSearchValue("")
            }}
            onInputChange={(value: string, actionMeta: any) => {
                if (actionMeta.action === "input-change" || actionMeta.action === "clear") {
                    setSearchValue(value)
                    if (value !== "") {
                        setIsSearching(true)
                    }
                }
            }}
            options={options}
            isDisabled={isDisabled}
            onChange={(selected: any) => {
                onChange(selected)
                if (selectRef.current) {
                    selectRef.current.blur();
                }
            }}
            components={(options.length > 7 || options.length === 0) && hasNextPage ? { MenuList: CustomMenuList } : {}}
            required
        />
    )
}

export default CustomerSelectWrapper;